import React, { useState, useRef, useEffect } from "react";
import useBreakpoint from "@/hooks/useBreakpoint";
import { Image } from "../../atoms/Image";

import "./styles.scss";

export const CustomSliderRange = ({
  list,
  defaultValue,
  initialDefault,
  defaultTextToSelected = "",
  onSelect,
  isGreatterValue,
}) => {
  const [position, setPosition] = useState(1);
  const [initialized, setInitialized] = useState(false);
  const [dragOffset, setDragOffset] = useState(null);

  const isDesktop = useBreakpoint("large-min");

  const sliderRef = useRef(null);
  const isDragging = useRef(false);
  const positionRef = useRef(1);
  const dragStartPosition = useRef(1);
  const dragOffsetRef = useRef(null);

  const numItems = list.length;

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  useEffect(() => {
    if (list.length > 0 && initialized && !isDragging.current) {
      onSelect(list[position - 1]);
    }
  }, [position, initialized]);

  useEffect(() => {
    if (defaultValue && list?.length > 0) {
      const initialDefaultIndex = list.findIndex((item) => item.text === initialDefault?.text);
      const index = list.findIndex((item) => item.text === defaultValue.text);
      const newPosition = index < 0 && !isDragging.current ? initialDefaultIndex + 1 : index < 0 ? 1 : index + 1;
      setPosition(newPosition);
      setInitialized(true);
    }
  }, [defaultValue, list, isGreatterValue]);

  const handleSelect = (newPosition, e) => {
    const stepElement = e.target.getBoundingClientRect();
    const clickPosition = e.clientX - stepElement.left;
    const stepWidth = stepElement.width;
    const percentageClick = (clickPosition / stepWidth) * 100;

    if (newPosition === 0) {
      setPosition(1);
      return;
    }

    setPosition(percentageClick > 50 ? newPosition + 1 : newPosition);
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;

    const sliderRect = sliderRef.current.getBoundingClientRect();
    const positionX = e.clientX - sliderRect.left;
    updatePosition(positionX, sliderRect.width);
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;

    const sliderRect = sliderRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const positionX = touch.clientX - sliderRect.left;
    updatePosition(positionX, sliderRect.width);
  };

  const updatePosition = (positionX, contentWidth) => {
    const clamped = Math.max(0, Math.min(positionX, contentWidth));
    dragOffsetRef.current = clamped;
    setDragOffset(clamped);
  };

  const snapToStep = () => {
    if (!sliderRef.current || dragOffsetRef.current == null) return;

    const contentWidth = sliderRef.current.getBoundingClientRect().width;
    const stepWidth = contentWidth / numItems;
    const rawStep = dragOffsetRef.current / stepWidth;

    const snapped = Math.max(
      1,
      Math.min(Math.round(rawStep + 0.0001), numItems),
    );

    dragOffsetRef.current = null;
    setDragOffset(null);
    setPosition(snapped);
  };

  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragStartPosition.current = positionRef.current;
    document.body.style.cursor = "grabbing";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e) => {
    isDragging.current = true;
    dragStartPosition.current = positionRef.current;
    document.body.style.cursor = "grabbing";
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);
  };

  const handleDragEnd = () => {
    isDragging.current = false;
    document.body.style.cursor = "";

    snapToStep();
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);

    handleDragEnd();
  };

  const handleTouchEnd = () => {
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);

    handleDragEnd();
  };

  const calculatePositionThumb = () => {
    if (dragOffset != null) {
      return `${dragOffset - 14}px`;
    }
    const lastPosition = numItems;
    return `calc(${(position / numItems) * 100}% + ${position *
      (3 / (numItems - 1))}px - 14px${
      lastPosition === position ? " - 13px" : ""
    })`;
  };

  const calculateTrackFillWidth = () => {
    if (dragOffset != null) {
      return `${dragOffset}px`;
    }
    return `${(position / numItems) * 100}%`;
  };

  return (
    <div className="flex items-center mt-10 gap-ride-gl-spacing-xlarge">
      <div ref={sliderRef} className="custom-slider-range">
        <div className="slider-track">
          <div
            className="slider-track-fill"
            style={{ width: calculateTrackFillWidth() }}
          />
          {list.map((_, i) => (
            <div
              key={`slide-${i}`}
              className={`slider-step ${
                position - 1 === i && (isDesktop ? numItems < 7 : numItems < 9)
                  ? "step-left-position"
                  : position === i && (isDesktop ? numItems < 7 : numItems < 9)
                  ? "step-right-position"
                  : ""
              }`}
              style={{
                width: `calc(100% / ${numItems})`,
              }}
              onClick={(e) => handleSelect(i, e)}
            >
              <div className="step-wrapper" />
              <div className="step-bar" />
              <div
                className={`mt-ride-gl-spacing-2xlarge w-max ml-auto absolute right-0 transform -top-14 ${
                  position === list.length ? "translate-x-3" : "translate-x-1/2"
                }`}
              >
                {position - 1 === i && (
                  <div className="badge-slider-step">
                    {_.text}
                    {defaultTextToSelected && ` ${defaultTextToSelected}`}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div
          className="slider-handle"
          style={{ left: calculatePositionThumb() }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <Image
            pngSrc="/ventadigital/devolucion/assets/vida-devolucion/plans/union.svg"
            alt="Icon thumb "
            fetchpriority="low"
            style={{ width: "100%", pointerEvents: "none" }}
          />
        </div>
      </div>
    </div>
  );
};
